import React from "react";
import { SearchMenu, Pill, PillWrapper, isNullish } from "@theorgchartdev/storybook";
import {
  useSelectableSearch,
  SelectableItem,
  UseSelectableSearchProps
} from "@/hooks/useSelectableSearch";

type SearchMenuOption = {
  value: string;
  content?: React.ReactNode;
  label?: string;
  clickHandler?: () => void;
  dataObj?: unknown;
  disabled?: boolean;
  className?: string;
  divider?: boolean;
  enableHoverBackground?: boolean;
};

export type SelectableOptionsMenuProps<T> = Pick<
  UseSelectableSearchProps<T>,
  "getItemId" | "getItemLabel" | "maxItems"
> & {
  selectedItems: T[];
  onAdd: (item: T) => void;
  onRemove: (item: T) => void;
  options: SearchMenuOption[];
  searchCallback?: (query: string) => void;
  placeholder?: string;
  disabled?: boolean;
  pillSize?: "small" | "medium";
  expandable?: boolean;
  dataTestId?: string;
  onResetInput?: () => void;
  fullWidth?: boolean;
};

const createSinglePill = <T,>(
  item: SelectableItem<T>,
  onRemove: ((item: SelectableItem<T>) => void) | null,
  disabled: boolean,
  size: "small" | "medium" = "medium",
  key: string = "0"
) => (
  <PillWrapper key={key}>
    <Pill
      size={size === "small" ? "compact" : "regular"}
      onRemove={
        onRemove
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              onRemove(item);
            }
          : undefined
      }
      disabled={disabled}
      label={item.label}
    />
  </PillWrapper>
);

const createMultiplePills = <T,>(
  items: SelectableItem<T>[],
  onRemove: ((item: SelectableItem<T>) => void) | null,
  disabled: boolean,
  size: "small" | "medium" = "medium"
) => (
  <>
    {items.map((item, index) =>
      createSinglePill(item, onRemove, disabled, size, item.id || String(index))
    )}
  </>
);

export const SelectableOptionsMenu = <T,>({
  selectedItems: items,
  getItemId,
  getItemLabel,
  onAdd,
  onRemove,
  maxItems,
  options,
  searchCallback,
  placeholder,
  disabled = false,
  pillSize = "medium",
  expandable = false,
  dataTestId,
  onResetInput,
  fullWidth = false
}: SelectableOptionsMenuProps<T>) => {
  const { selectedItems, handleRemove, searchRef, canAddMore } = useSelectableSearch({
    items,
    getItemId,
    getItemLabel,
    onAdd,
    onRemove,
    maxItems
  });

  const hasSingleItem = selectedItems.length === 1;
  const hasMultipleItems = selectedItems.length > 1;
  const showSinglePill = hasSingleItem;
  const showMultiplePills = hasMultipleItems;

  const pillPrefix = showMultiplePills
    ? createMultiplePills(selectedItems, disabled ? null : handleRemove, disabled, pillSize)
    : showSinglePill
      ? createSinglePill(selectedItems[0], disabled ? null : handleRemove, disabled, pillSize)
      : undefined;

  const wrappedOptions = options.map(option => {
    if (option.disabled || isNullish(option.clickHandler)) {
      return option;
    }

    return {
      ...option,
      clickHandler: () => {
        if (!canAddMore) return;

        option.clickHandler?.();

        if (searchRef.current?.setValue) {
          searchRef.current.setValue("", true);
        }
      }
    };
  });

  const wrapperClass = fullWidth ? "ocn-basic-search-wrapper-full-width" : "";

  return (
    <span className={wrapperClass}>
      <SearchMenu
        placeholder={placeholder}
        searchSize={"medium"}
        options={wrappedOptions}
        optionSize={"small"}
        searchCallback={searchCallback ?? (() => {})}
        listWidth={"large"}
        loading={false}
        defaultInputVal={""}
        searchProps={{
          handleRef: searchRef,
          prefixNode: pillPrefix,
          expandable: expandable || showMultiplePills,
          onResetInput: onResetInput,
          dataTestId: dataTestId
        }}
        inputProps={{
          disabled
        }}
      />
    </span>
  );
};

export default SelectableOptionsMenu;
